import React from "react";
import { useQuery } from "@apollo/client";
import { QUERY_CAR_STATUS } from "../../../../utils/graphql/queries/TPMSQueries";
import { IdsContext } from "../IdSwitch/IdsContext";
import styles from "./style.module.scss";

interface DriverName {
  carNum: string;
  name: string;
  team?: string;
  color?: string;
}

interface CarTpms {
  frontLeft: string | number;
  frontRight: string | number;
  rearLeft: string | number;
  rearRight: string | number;
}

interface CarStatus {
  carNum: string;
  status: string;
  tpms: CarTpms;
}

interface CurrentTyresButtonProps {
  onClick: () => void;
}

export const CurrentTyresButton: React.FC<CurrentTyresButtonProps> = ({
  onClick,
}) => {
  return (
    <button className={styles.openButton} onClick={onClick}>
      Current Tyres
    </button>
  );
};

interface CurrentTyresModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventCode: string;
  driverNames: DriverName[];
}

export const CurrentTyresModal: React.FC<CurrentTyresModalProps> = ({
  isOpen,
  onClose,
  eventCode,
  driverNames,
}) => {
  const idsContext = React.useContext(IdsContext);
  const { data: carStatusData, loading } = useQuery(QUERY_CAR_STATUS, {
    variables: { eventCode },
    skip: !eventCode || !isOpen,
    fetchPolicy: "network-only",
  });

  if (!isOpen) return null;

  const carStatusList = carStatusData?.carStatus ?? [];

  const getDriverName = (carNum: string) => {
    const driver = driverNames.find((d) => d.carNum === carNum);
    return driver?.name || carNum;
  };

  const getTyreCode = (carNum: string, tyreId: string | number) => {
    if (!idsContext || !tyreId) return "-";
    const { showCodes, getCodeForDriverAndId } = idsContext;
    if (!showCodes) return tyreId;
    return getCodeForDriverAndId(carNum, String(tyreId));
  };

  const getTextColorForBackground = (backgroundColor: string) => {
    return backgroundColor?.toLowerCase().trim() === "rgb(0, 0, 0)"
      ? "#fff"
      : "#000";
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>Current Tyres</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.content}>
          <p>Event Code: {eventCode || "No event selected"}</p>
          {loading && <p>Loading...</p>}
          {!loading && carStatusList.length === 0 && (
            <p>No car status data available</p>
          )}
          {!loading && carStatusList.length > 0 && (
            <>
              {/* TRACK Cars */}
              <div>
                <h4 style={{ color: "#4caf50", marginTop: "16px" }}>
                  On Track
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {carStatusList
                    .filter((car: CarStatus) => car.status === "TRACK")
                    .sort((a: CarStatus, b: CarStatus) => {
                      const driverA = driverNames.find(
                        (d) => d.carNum === a.carNum,
                      );
                      const driverB = driverNames.find(
                        (d) => d.carNum === b.carNum,
                      );
                      const teamA = driverA?.team || "Unknown";
                      const teamB = driverB?.team || "Unknown";
                      if (teamA !== teamB) {
                        return teamB.localeCompare(teamA);
                      }
                      return b.carNum.localeCompare(a.carNum);
                    })
                    .map((car: CarStatus) => {
                      const driver = driverNames.find(
                        (d) => d.carNum === car.carNum,
                      );
                      return (
                        <div
                          key={car.carNum}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: driver?.color || "#666",
                              padding: "8px 12px",
                              borderRadius: "4px",
                              width: "100%",
                              color: getTextColorForBackground(
                                driver?.color || "#666",
                              ),
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            {getDriverName(car.carNum)}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                border: "1px solid #4caf50",
                                padding: "8px",
                                textAlign: "center",
                                backgroundColor: "rgba(76, 175, 80, 0.1)",
                              }}
                            >
                              {getTyreCode(car.carNum, car.tpms?.frontLeft)}
                            </div>
                            <div
                              style={{
                                border: "1px solid #4caf50",
                                padding: "8px",
                                textAlign: "center",
                                backgroundColor: "rgba(76, 175, 80, 0.1)",
                              }}
                            >
                              {getTyreCode(car.carNum, car.tpms?.frontRight)}
                            </div>
                            <div
                              style={{
                                border: "1px solid #4caf50",
                                padding: "8px",
                                textAlign: "center",
                                backgroundColor: "rgba(76, 175, 80, 0.1)",
                              }}
                            >
                              {getTyreCode(car.carNum, car.tpms?.rearLeft)}
                            </div>
                            <div
                              style={{
                                border: "1px solid #4caf50",
                                padding: "8px",
                                textAlign: "center",
                                backgroundColor: "rgba(76, 175, 80, 0.1)",
                              }}
                            >
                              {getTyreCode(car.carNum, car.tpms?.rearRight)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Other Status Cars */}
              {carStatusList.some(
                (car: CarStatus) => car.status !== "TRACK",
              ) && (
                <div>
                  <h4 style={{ marginTop: "16px" }}>Other Status</h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    {carStatusList
                      .filter((car: CarStatus) => car.status !== "TRACK")
                      .sort((a: CarStatus, b: CarStatus) => {
                        const driverA = driverNames.find(
                          (d) => d.carNum === a.carNum,
                        );
                        const driverB = driverNames.find(
                          (d) => d.carNum === b.carNum,
                        );
                        const teamA = driverA?.team || "Unknown";
                        const teamB = driverB?.team || "Unknown";
                        if (teamA !== teamB) {
                          return teamB.localeCompare(teamA);
                        }
                        return b.carNum.localeCompare(a.carNum);
                      })
                      .map((car: CarStatus) => {
                        const driver = driverNames.find(
                          (d) => d.carNum === car.carNum,
                        );
                        return (
                          <div
                            key={car.carNum}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: driver?.color || "#666",
                                padding: "8px 12px",
                                borderRadius: "4px",
                                width: "100%",
                                color: getTextColorForBackground(
                                  driver?.color || "#666",
                                ),
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              {getDriverName(car.carNum)}
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "8px",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "8px",
                                  textAlign: "center",
                                }}
                              >
                                {getTyreCode(car.carNum, car.tpms?.frontLeft)}
                              </div>
                              <div
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "8px",
                                  textAlign: "center",
                                }}
                              >
                                {getTyreCode(car.carNum, car.tpms?.frontRight)}
                              </div>
                              <div
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "8px",
                                  textAlign: "center",
                                }}
                              >
                                {getTyreCode(car.carNum, car.tpms?.rearLeft)}
                              </div>
                              <div
                                style={{
                                  border: "1px solid #ccc",
                                  padding: "8px",
                                  textAlign: "center",
                                }}
                              >
                                {getTyreCode(car.carNum, car.tpms?.rearRight)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface CurrentTyresProps {
  eventCode: string;
  driverNames: DriverName[];
}

const CurrentTyres: React.FC<CurrentTyresProps> = ({
  eventCode,
  driverNames,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <CurrentTyresButton onClick={() => setIsOpen(true)} />
      <CurrentTyresModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        eventCode={eventCode}
        driverNames={driverNames}
      />
    </>
  );
};

export default CurrentTyres;
